// All writes, reconciliation reads and section publication share this queue.
// Never abort or automatically retry a write: a lost response can still mean a successful add.
export class CartError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'CartError';
    Object.assign(this, details);
  }
}
const propertiesKey = (value) =>
  JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
export function resolveLine(cart, reference) {
  const exact = cart.items.find((item) => item.key === reference.key);
  if (exact) return exact;
  const matches = cart.items.filter(
    (item) =>
      String(item.variant_id) === String(reference.variantId) &&
      String(item.selling_plan_allocation?.selling_plan?.id || '') ===
        String(reference.sellingPlanId || '') &&
      propertiesKey(item.properties) === propertiesKey(reference.properties),
  );
  if (matches.length !== 1)
    throw new CartError('Your bag has changed. Review it before updating this item.', {
      stale: true,
    });
  return matches[0];
}
export function createCartService(options = {}) {
  const fetcher = options.fetch || ((...args) => globalThis.fetch(...args));
  const root = () => (options.root || globalThis.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const sectionIDs = () => {
    const mainID = globalThis.document?.querySelector('[data-main-cart]')?.dataset.sectionId;
    return (options.sections?.() || ['cart-drawer', ...(mainID ? [mainID] : [])]).slice(0, 5);
  };
  let queue = Promise.resolve(),
    pending = 0;
  const enqueue = (task) => {
    if (++pending === 1) options.onBusy?.(true);
    const result = queue.then(task).finally(() => {
      pending--;
      if (!pending) options.onBusy?.(false);
    });
    queue = result.catch(() => {});
    return result;
  };
  async function request(url, init = {}) {
    let response, body;
    try {
      response = await fetcher(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...init,
        headers: { Accept: 'application/json', ...init.headers },
      });
      body = await response.json();
    } catch (cause) {
      throw new CartError('The connection was interrupted. Check your bag before trying again.', {
        uncertain: init.method === 'POST',
        cause,
      });
    }
    if (!response.ok || Number(body?.status) >= 400) {
      const message =
        typeof body?.description === 'string'
          ? body.description
          : typeof body?.message === 'string'
            ? body.message
            : 'Your bag could not be updated.';
      throw new CartError(message, {
        status: response.status,
        uncertain: init.method === 'POST' && response.status >= 500,
      });
    }
    return body;
  }
  async function read() {
    const cart = await request(`${root()}cart.js`);
    if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count))
      throw new CartError('Your bag could not be read. Open your bag to check it.');
    return cart;
  }
  async function publish(cart, supplied = {}) {
    const ids = sectionIDs();
    const sections = { ...supplied };
    const missing = ids.filter((id) => typeof sections[id] !== 'string');
    if (missing.length) {
      try {
        // /cart is an HTML route: forcing application/json can bypass section rendering.
        // The sections query still returns JSON with the browser's default Accept format.
        const fresh = await request(
          `${root()}cart?sections=${encodeURIComponent(missing.join(','))}`,
          { headers: { Accept: '*/*' } },
        );
        for (const id of missing) sections[id] = typeof fresh?.[id] === 'string' ? fresh[id] : null;
      } catch {
        for (const id of missing) sections[id] = null;
      }
    }
    const result = {
      cart,
      sections,
      missingSections: ids.filter((id) => typeof sections[id] !== 'string'),
    };
    await options.onChange?.(result);
    return result;
  }
  async function refreshNow() {
    return publish(await read());
  }
  async function mutate(action, payload) {
    const ids = sectionIDs();
    let body,
      headers = {};
    if (payload instanceof FormData) {
      body = new FormData();
      for (const [key, value] of payload) body.append(key, value);
      body.set('sections', ids.join(','));
      body.set('sections_url', `${root()}cart`);
    } else {
      body = JSON.stringify({ ...payload, sections: ids, sections_url: `${root()}cart` });
      headers['Content-Type'] = 'application/json';
    }
    let response;
    try {
      response = await request(`${root()}cart/${action}.js`, { method: 'POST', body, headers });
    } catch (error) {
      try {
        await refreshNow();
        error.reconciled = true;
      } catch {
        error.reconciled = false;
      }
      throw error;
    }
    try {
      // add.js returns added lines, not a complete cart. Read the authoritative cart.
      const cart = action === 'add' ? await read() : response;
      if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count))
        throw new Error('Invalid cart response');
      return await publish(cart, response.sections);
    } catch (cause) {
      throw new CartError(
        'Your item was updated, but the bag could not refresh. Open your bag to check it.',
        { confirmed: true, uncertain: false, cause },
      );
    }
  }
  return {
    add: (payload) => enqueue(() => mutate('add', payload)),
    refresh: () => enqueue(refreshNow),
    change: (reference, adjustment) =>
      enqueue(async () => {
        if (
          adjustment.quantity !== undefined &&
          (!Number.isSafeInteger(adjustment.quantity) || adjustment.quantity < 0)
        )
          throw new CartError('Enter a whole quantity of zero or more.');
        if (adjustment.delta !== undefined && !Number.isSafeInteger(adjustment.delta))
          throw new CartError('Enter a whole quantity.');
        const cart = await read();
        let line;
        try {
          line = resolveLine(cart, reference);
        } catch (error) {
          await publish(cart);
          throw error;
        }
        const quantity = adjustment.quantity ?? Math.max(0, line.quantity + adjustment.delta);
        if (!Number.isSafeInteger(quantity) || quantity < 0)
          throw new CartError('Enter a whole quantity of zero or more.');
        return mutate('change', { id: line.key, quantity });
      }),
  };
}
export const cartService = createCartService({
  onBusy: (busy) =>
    globalThis.document?.dispatchEvent(new CustomEvent('aura:cart-busy', { detail: { busy } })),
  onChange: (result) =>
    globalThis.document?.dispatchEvent(new CustomEvent('aura:cart-change', { detail: result })),
});
